import { resolveImageUrl as resolveImageUrlFromUtils } from "../../../utils/imageUtils";

export function pickFirst(obj, keys) {
    if (!obj) return "";
    for (const key of keys) {
        const value = obj[key];
        if (typeof value === "string" && value.trim() !== "") {
            return value.trim();
        }
    }
    return "";
}

/** Re-export shared resolver (uses production media host when API is local/dev). */
export function resolveImageUrl(value) {
    return resolveImageUrlFromUtils(value);
}

export function formatTodayAvailability(info) {
    const today = new Date().toLocaleDateString(undefined, { weekday: "long" });
    const todayHours = pickFirst(info, [
        "todayHours",
        "TodayHours",
        "openingHours",
        "OpeningHours",
        "hours",
        "Hours",
    ]);

    if (!todayHours || typeof todayHours !== "string") {
        return "Available today";
    }

    if (todayHours.includes("00:00")) {
        return "Available today";
    }

    const lower = todayHours.toLowerCase();
    const lowerToday = today.toLowerCase();
    if (!lower.includes(lowerToday) && /monday|tuesday|wednesday|thursday|friday|saturday|sunday/.test(lower)) {
        return "Available today";
    }

    const parts = todayHours.split(/,|-/).map((p) => p.trim());
    const firstPart = parts[0] || todayHours;
    return `Available ${firstPart}`;
}

export function buildAddressText(info) {
    const addressParts = [
        pickFirst(info, ["addressLine1", "AddressLine1"]),
        pickFirst(info, ["addressLine2", "AddressLine2"]),
        pickFirst(info, ["city", "City"]),
        pickFirst(info, ["postalCode", "PostalCode", "postCode", "PostCode"]),
        pickFirst(info, ["country", "Country"]),
        pickFirst(info, ["address", "Address"]),
    ].filter(Boolean);

    return addressParts.length > 0 ? addressParts.join(", ") : "Address not available";
}

export function getRestaurantDisplayName(info) {
    return pickFirst(info, [
        "name",
        "Name",
        "restaurantName",
        "RestaurantName",
        "compName",
        "Comp_Name",
    ]);
}

const DEFAULT_DESCRIPTION =
    "Sandwich · Breakfast and Brunch · Salads · Healthy · Group Friendly";

export function getMenuHeroDescription(info) {
    return (
        pickFirst(info, ["description", "Description", "shortDescription", "ShortDescription"]) ||
        DEFAULT_DESCRIPTION
    );
}

export function getShortCompanyDetail(info) {
    const short = pickFirst(info, ["shortDescription", "ShortDescription"]);
    const long = pickFirst(info, ["description", "Description"]);
    const combined = (short || long || "").trim();
    return combined || "Short summary not available yet.";
}

export function getGeneralTimeText(info) {
    const preset = pickFirst(info, ["todayAvailability", "TodayAvailability"]);
    if (preset) return preset;
    return formatTodayAvailability(info) || "Hours not available";
}

export function getDeliveryTimeDisplay(info) {
    if (!info || typeof info !== "object") {
        return "Delivery is not Available Today";
    }
    const n = info.s_DeliveryTime ?? info.S_DeliveryTime;
    if (typeof n === "number" && n > 0) {
        return `It would roughly take ${n} mins to deliver your order`;
    }
    if (typeof n === "string" && /^\d+$/.test(n.trim()) && parseInt(n, 10) > 0) {
        return `It would roughly take ${n.trim()} mins to deliver your order`;
    }
    const deliveryTimeText = pickFirst(info, ["deliveryTime", "DeliveryTime", "s_DeliveryTime"]);
    return deliveryTimeText ? deliveryTimeText : "Delivery is not Available Today";
}

export function getCollectionTimeDisplay(info) {
    const collectionTimeText = pickFirst(info, [
        "collectionTime",
        "CollectionTime",
        "pickupTime",
        "PickupTime",
        "s_CollectionTime",
    ]);
    return collectionTimeText ? collectionTimeText : "Pick-Up is not Available Today";
}

export function getResolvedCoverSrc(info, placeholderPath) {
    const flat = info && typeof info === "object" ? info : {};
    const nestedObjects = [
        flat.company,
        flat.Company,
        flat.companyDetails,
        flat.CompanyDetails,
        flat.restaurant,
        flat.Restaurant,
        flat.restaurantProfile,
        flat.RestaurantProfile,
        flat.location,
        flat.Location,
        flat.store,
        flat.Store,
    ].filter((o) => o && typeof o === "object");

    const coverKeys = [
        "coverImage",
        "CoverImage",
        "coverImageUrl",
        "CoverImageUrl",
        "image",
        "Image",
        "bannerImage",
        "BannerImage",
        "backgroundImage",
        "BackgroundImage",
        "Comp_BackgroundImage",
        "Company_BackgroundImage",
        "storeImage",
        "StoreImage",
        "restaurantImage",
        "RestaurantImage",
        "featuredImage",
        "FeaturedImage",
        "headerImage",
        "HeaderImage",
        "mainImage",
        "MainImage",
        "bannerUrl",
        "BannerUrl",
        "imageUrl",
        "ImageUrl",
    ];

    let raw = pickFirst(flat, coverKeys);
    if (!raw) {
        for (const nest of nestedObjects) {
            raw = pickFirst(nest, coverKeys);
            if (raw) break;
        }
    }
    return resolveImageUrl(raw) || placeholderPath;
}

export function getResolvedLogoSrc(info, placeholderPath) {
    const flat = info && typeof info === "object" ? info : {};
    const nestedObjects = [
        flat.company,
        flat.Company,
        flat.companyDetails,
        flat.CompanyDetails,
        flat.restaurant,
        flat.Restaurant,
        flat.location,
        flat.Location,
    ].filter((o) => o && typeof o === "object");

    const logoKeys = [
        "logo",
        "Logo",
        "logoUrl",
        "LogoUrl",
        "companyLogo",
        "Company_Logo",
        "Comp_Logo",
        "CompIcon",
        "compIcon",
        "storeLogo",
        "StoreLogo",
        "brandLogo",
        "BrandLogo",
    ];

    let raw = pickFirst(flat, logoKeys);
    if (!raw) {
        for (const nest of nestedObjects) {
            raw = pickFirst(nest, logoKeys);
            if (raw) break;
        }
    }
    return resolveImageUrl(raw) || placeholderPath;
}

/** Generic restaurant tile — not the ServeIQ product mark */
export const RESTAURANT_LOGO_PLACEHOLDER = "/assets/img/restaurant-logo.png";

/**
 * Ordered logo URLs from restaurant payload (absolute API URL preferred).
 * @returns {string[]}
 */
export function getRestaurantLogoCandidates(info) {
    const flat = info && typeof info === "object" ? info : {};
    const resolved = getResolvedLogoSrc(flat, "");
    const raw = pickFirst(flat, [
        "logo",
        "Logo",
        "logoUrl",
        "LogoUrl",
        "companyLogo",
        "Company_Logo",
        "Comp_Logo",
        "CompIcon",
        "compIcon",
        "storeLogo",
        "StoreLogo",
        "brandLogo",
        "BrandLogo",
    ]);
    const fromRaw = raw ? resolveImageUrl(raw) : "";
    const trimmed = typeof raw === "string" ? raw.trim() : "";
    const list = [resolved, fromRaw, trimmed].filter((url) => typeof url === "string" && url.length > 0);
    return [...new Set(list)];
}

/** Primary logo for header / order cards — same source everywhere */
export function getPrimaryRestaurantLogoSrc(
    info,
    placeholder = RESTAURANT_LOGO_PLACEHOLDER
) {
    const candidates = getRestaurantLogoCandidates(info);
    return candidates[0] || placeholder;
}

export function buildInfoRows(info, companySettings = null) {
    const addressText = buildAddressText(info);
    // Prefer dedicated online-ordering contacts from settings / restaurant payload
    const phoneFromSettings = (
        companySettings?.s_OnlineOrderingPhone ||
        companySettings?.S_OnlineOrderingPhone ||
        companySettings?.onlineOrderingPhone ||
        ""
    ).toString().trim();
    const emailFromSettings = (
        companySettings?.s_OnlineOrderingEmail ||
        companySettings?.S_OnlineOrderingEmail ||
        companySettings?.onlineOrderingEmail ||
        ""
    ).toString().trim();
    const phone =
        phoneFromSettings ||
        pickFirst(info, [
            "onlineOrderingPhone",
            "OnlineOrderingPhone",
            "s_OnlineOrderingPhone",
            "S_OnlineOrderingPhone",
            "phone",
            "Phone",
            "phoneNo",
            "PhoneNo",
        ]);
    const email =
        emailFromSettings ||
        pickFirst(info, [
            "onlineOrderingEmail",
            "OnlineOrderingEmail",
            "s_OnlineOrderingEmail",
            "S_OnlineOrderingEmail",
            "email",
            "Email",
        ]);
    const rows = [
        { label: "Address", value: addressText },
        { label: "Phone", value: phone },
        { label: "Email", value: email },
        {
            label: "Facebook",
            value: pickFirst(info, ["facebookLink", "FacebookLink", "facebook", "Facebook"]),
            isLink: true,
            icon: "facebook",
        },
        {
            label: "Instagram",
            value: pickFirst(info, ["instagramLink", "InstagramLink", "instagram", "Instagram"]),
            isLink: true,
            icon: "instagram",
        },
        {
            label: "YouTube",
            value: pickFirst(info, ["youtubeLink", "YoutubeLink", "YouTubeLink", "youtube", "Youtube"]),
            isLink: true,
            icon: "youtube",
        },
        {
            label: "TikTok",
            value: pickFirst(info, ["tiktokLink", "TikTokLink", "TiktokLink", "tiktok", "TikTok"]),
            isLink: true,
            icon: "tiktok",
        },
    ];
    return rows.filter((row) => row.value && String(row.value).trim() !== "");
}
